// BarChartWrapper.jsx

import React, { useRef, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import styles from "./style.module.scss";
import CustomChart from "components/Charts";
import { useTranslation } from "react-i18next";
import { intentLabelToStackedBarRgba } from "constants/dashboardIntentColors";
import { sentimentLabelToStackedBarRgba } from "constants/dashboardSentimentColors";
import {
  buildStackedBarMixedInnerTooltipHtml,
  isMixedSegmentLabel,
  positionStackedBarMixedInnerTooltip,
} from "./stackedBarMixedInnerTooltip";

const BarChartWrapper = ({
  data,
  selectedTopics,
  setSelectedTopics,
  metric,
  stacked,
  disableSelection,
  onlyOneMetric,
  scrollable,
  /** Metric 12: stable intent segment order from API datasets. */
  intentSegmentOrder,
  /** When set (e.g. Welcome metric 14 General bar → topic tab), bypass dashboard topic hierarchy selection. */
  onStackedBarClick,
  /** Metric 14: (barIndex, datasetIndex) when clicking a stacked segment (negative/neutral/positive). */
  onSentimentStackSegmentClick,
  /** Metric 15: dataset label from API (e.g. Participation). */
  participationDatasetLabel,
}) => {
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const chartRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 950);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const sortedDataRef = useRef(data);
  const intentTooltipIdRef = useRef(null);
  if (!intentTooltipIdRef.current) {
    intentTooltipIdRef.current = `intent-stacked-tooltip-${Math.random()
      .toString(36)
      .slice(2, 11)}`;
  }
  const intentTooltipId = intentTooltipIdRef.current;
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      const el = document.getElementById(intentTooltipId);
      if (el) {
        el.remove();
      }
    };
  }, [intentTooltipId]);

  const resolveMixedInnerBreakdown = useCallback((topicData, activeMetric) => {
    if (Array.isArray(topicData?.mixedInnerBreakdown)) {
      return topicData.mixedInnerBreakdown;
    }
    if (activeMetric === "intent") {
      const intent = topicData?.intent;
      if (!intent || typeof intent !== "object") {
        return [];
      }
      const mixedKey = Object.keys(intent).find(
        (key) => String(key).toLowerCase() === "mixed",
      );
      if (!mixedKey) {
        return [];
      }
      const inner = intent[mixedKey]?.innerBreakdown;
      return Array.isArray(inner) ? inner : [];
    }
    if (activeMetric === "sentiment") {
      const sentiment = topicData?.sentiment;
      if (!sentiment || typeof sentiment !== "object") {
        return [];
      }
      for (const key of ["mixed", "33", "Mixed"]) {
        const inner = sentiment[key]?.innerBreakdown;
        if (Array.isArray(inner)) {
          return inner;
        }
      }
    }
    return [];
  }, []);

  const sentimentColorForTooltipLabel = useCallback(
    (label) => {
      const raw = String(label ?? "").trim().toLowerCase();
      if (
        raw === "positive" ||
        label === t("feedback_dropdown_values.2")
      ) {
        return sentimentLabelToStackedBarRgba("positive");
      }
      if (
        raw === "neutral" ||
        label === t("feedback_dropdown_values.3")
      ) {
        return sentimentLabelToStackedBarRgba("neutral");
      }
      if (
        raw === "negative" ||
        label === t("feedback_dropdown_values.4")
      ) {
        return sentimentLabelToStackedBarRgba("negative");
      }
      if (
        raw === "mixed" ||
        label === t("feedback_dropdown_values.13")
      ) {
        return sentimentLabelToStackedBarRgba("mixed");
      }
      if (
        raw === "unspecified" ||
        label === t("feedback_dropdown_values.1")
      ) {
        return sentimentLabelToStackedBarRgba("unspecified");
      }
      return sentimentLabelToStackedBarRgba(label);
    },
    [t],
  );

  const augmentSegmentRowsWithMixed = useCallback(
    (segmentRows, topicData, activeMetric) => {
      if (activeMetric !== "sentiment") {
        return segmentRows;
      }
      const rows = Array.isArray(segmentRows) ? [...segmentRows] : [];
      const hasMixed = rows.some((row) => isMixedSegmentLabel(row?.label));
      if (hasMixed) {
        return rows;
      }
      const mixedPct = Number(topicData?.mixedPercentage);
      const inner = resolveMixedInnerBreakdown(topicData, activeMetric);
      if (
        !Number.isFinite(mixedPct) ||
        mixedPct <= 0 ||
        !inner.length
      ) {
        return rows;
      }
      rows.push({ label: t("feedback_dropdown_values.13", "Mixed"), value: mixedPct });
      return rows;
    },
    [resolveMixedInnerBreakdown, t],
  );

  const renderStackedMixedInnerExternalTooltip = useCallback(
    (context) => {
      let tooltipEl = document.getElementById(intentTooltipId);
      if (!tooltipEl) {
        tooltipEl = document.createElement("div");
        tooltipEl.id = intentTooltipId;
        document.body.appendChild(tooltipEl);
      }

      const tooltipModel = context?.tooltip;
      if (!tooltipModel || tooltipModel.opacity === 0) {
        tooltipEl.style.opacity = "0";
        return;
      }

      const dataIndex = tooltipModel.dataPoints?.[0]?.dataIndex;
      const topicData =
        dataIndex != null && sortedDataRef.current
          ? sortedDataRef.current[dataIndex]
          : null;
      if (!topicData) {
        tooltipEl.style.opacity = "0";
        return;
      }

      const activeMetric = metric;
      const segmentRows = augmentSegmentRowsWithMixed(
        (tooltipModel.dataPoints || []).map((point) => ({
          label: point.dataset?.label || point.label,
          value: point.parsed?.y ?? point.raw ?? 0,
        })),
        topicData,
        activeMetric,
      );

      const colorForLabel =
        activeMetric === "sentiment"
          ? sentimentColorForTooltipLabel
          : (label) => intentLabelToStackedBarRgba(label, 0.8);

      tooltipEl.innerHTML = buildStackedBarMixedInnerTooltipHtml({
        styles,
        topicLabel: topicData.label,
        reviewCount: topicData.count || 0,
        segmentRows,
        mixedInnerBreakdown: resolveMixedInnerBreakdown(
          topicData,
          activeMetric,
        ),
        totalReviewsLabel: t("common.total_reviews"),
        colorForLabel,
      });

      const canvas = context.chart?.canvas;
      if (!canvas) {
        return;
      }
      positionStackedBarMixedInnerTooltip(
        tooltipEl,
        canvas.getBoundingClientRect(),
        tooltipModel.caretX,
        tooltipModel.caretY,
      );
    },
    [
      intentTooltipId,
      metric,
      augmentSegmentRowsWithMixed,
      resolveMixedInnerBreakdown,
      sentimentColorForTooltipLabel,
      t,
    ],
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 950);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update container width when data changes
  useEffect(() => {
    if (containerRef?.current) {
      setContainerWidth(containerRef?.current?.offsetWidth);
    }
  }, [data]);

  const getBarThickness = useCallback(
    (dataLength = data.length) => {
      const noOfBars = dataLength;

      return noOfBars > 30
        ? "flex"
        : (isMobileView && noOfBars > 12) ||
          (window?.innerWidth > 1250 && noOfBars > 8 && !onlyOneMetric)
        ? 16
        : onlyOneMetric && noOfBars < 12
        ? 28
        : 24;
    },
    [data.length, isMobileView, onlyOneMetric]
  );

  const removeParentFromChildren = (parent) => {
    if (parent && parent !== "") {
      const splitLabel = parent.split(" > ");
      if (splitLabel.length > 1) {
        splitLabel.shift();
        return splitLabel.join(" > ");
      }
      return splitLabel;
    }
    return parent;
  };

  // Calculate if scrolling is needed based on width
  const shouldEnableScrolling = useCallback(() => {
    if (!scrollable) return;

    const noOfBars = data.length;
    const barThickness = getBarThickness(noOfBars);

    // Calculate bar width (including spacing)
    const barWidth = barThickness === "flex" ? 40 : barThickness + 20;
    const padding = 120; // Chart padding (left + right margins + y-axis)

    const requiredWidth = noOfBars * barWidth + padding;

    return requiredWidth > containerWidth && containerWidth > 0;
  }, [scrollable, data.length, containerWidth, getBarThickness]);

  const getChartWidth = useCallback(() => {
    if (!shouldEnableScrolling()) {
      return "100%";
    }

    const noOfBars = data.length;
    const barThickness = getBarThickness();
    const barWidth = barThickness === "flex" ? 40 : barThickness + 20;
    const padding = 80;

    const calculatedWidth = noOfBars * barWidth + padding;

    return `${calculatedWidth}px`;
  }, [data.length, shouldEnableScrolling, getBarThickness]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!scrollContainerRef.current || !shouldEnableScrolling()) return;

      const container = scrollContainerRef.current;
      const scrollAmount = 100;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        container.scrollLeft = Math.max(0, container.scrollLeft - scrollAmount);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        container.scrollLeft = Math.min(
          container.scrollWidth - container.clientWidth,
          container.scrollLeft + scrollAmount
        );
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("keydown", handleKeyDown);
      return () => container.removeEventListener("keydown", handleKeyDown);
    }
  }, [shouldEnableScrolling]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (!scrollContainerRef.current || !shouldEnableScrolling()) return;

      if (e.shiftKey && Math.abs(e.deltaY) > 0) {
        e.preventDefault();
        scrollContainerRef.current.scrollLeft += e.deltaY;
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      return () => container.removeEventListener("wheel", handleWheel);
    }
  }, [shouldEnableScrolling]);

  const handleScrollStart = () => {
    setIsScrolling(true);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 500);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (shouldEnableScrolling()) {
        handleScrollStart();
      }
    };

    const container = scrollContainerRef?.current;
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [shouldEnableScrolling]);

  // Sort by reviewsCount if metric === "rating"; by count for Welcome intent (metric 12).
  let sortedData = data;
  if (metric === "rating") {
    sortedData = [...data].sort(
      (a, b) => (b[metric]?.reviewsCount || 0) - (a[metric]?.reviewsCount || 0)
    );
  } else if (metric === "intent" || metric === "participation") {
    const hasComparableCounts = data.some((item) => (item.count || 0) > 0);
    if (hasComparableCounts) {
      sortedData = [...data].sort((a, b) => {
        if ((b.count || 0) !== (a.count || 0)) {
          return (b.count || 0) - (a.count || 0);
        }
        return String(a.label || "").localeCompare(
          String(b.label || ""),
          undefined,
          { sensitivity: "base" },
        );
      });
    }
  }

  sortedDataRef.current = sortedData;

  const labels = sortedData.map((item) => item.label);
  const ids = sortedData.map((item) => item.id);
  const children = sortedData.map((item) => item?.child);
  const values = sortedData.map((item) => item[metric]); // for non-rating
  const barThickness = getBarThickness();

  // Intent stacked bars have chart.js legend disabled, so colors are only
  // readable on hover. Build a static legend from the segments actually present.
  const intentLegendSegments =
    metric === "intent" && stacked
      ? Array.isArray(intentSegmentOrder) && intentSegmentOrder.length
        ? intentSegmentOrder
        : sortedData.reduce((acc, item) => {
            for (const k of Object.keys(item.intent || {})) {
              if (!acc.includes(k)) acc.push(k);
            }
            return acc;
          }, [])
      : [];

  let chartData;
  if (metric === "rating") {
    const reviewCounts = sortedData.map(
      (item) => item[metric]?.reviewsCount || 0
    );
    const avgRatings = sortedData.map((item) => item[metric]?.value ?? 0);
    const avgImpact = sortedData.map((item) => item?.potential ?? 0);

    chartData = {
      labels,
      datasets: [
        {
          type: "bar",
          label: t("dashboard_charts.reviews_count", "Reviews Count"),
          data: reviewCounts,
          backgroundColor: "#6A0DAD",
          borderColor: "#6A0DAD",
          borderWidth: 1,
          datalabels: {
            display: true,
            color: theme === "dark" ? "#fff" : "#000",
            anchor: "end",
            align: "start",
            offset: -20,
            font: { size: 12 },
            formatter: (val) => (typeof val === "number" ? val : ""),
          },
          barThickness,
          categoryPercentage: 1.0,
          barPercentage: 0.7,
          borderRadius: 5,
        },
        {
          type: "line",
          label: t("dashboard_charts.avg_rating", "Avg Rating"),
          data: avgRatings,
          borderColor: theme === "dark" ? "#fff" : "#000",
          backgroundColor: theme === "dark" ? "#fff" : "#000",
          borderWidth: 1,
          tension: 0.2,
          yAxisID: "y1",
          fill: false,
          pointRadius: 4,
          pointBackgroundColor: theme === "dark" ? "#fff" : "#000",
          pointBorderColor: theme === "dark" ? "#fff" : "#000",
          pointBorderWidth: 1,
          datalabels: {
            display: true,
            color: theme === "dark" ? "#fff" : "#000",
            anchor: "end",
            align: "bottom",
            offset: -4,
            font: { size: 12 },
            formatter: (val, ctx) => {
              const xLabel = ctx.chart.data.labels[ctx.dataIndex];
              return typeof val === "number"
                ? `${xLabel}: ${val.toFixed(1)}`
                : "";
            },
          },
        },
        {
          type: "line",
          label: t("dashboard_charts.contribution_to_rating", "Contribution to Rating"),
          data: avgImpact,
          borderColor: "#FF6B35",
          backgroundColor: "#FF6B35",
          borderWidth: 0,
          tension: 0.6,
          yAxisID: "y1",
          fill: false,
          pointRadius: 0,
          pointBackgroundColor: "#FF6B35",
          pointBorderColor: "#FF6B35",
          pointBorderWidth: 1,
          datalabels: {
            display: false,
            color: "#FF6B35",
            anchor: "end",
            align: "bottom",
            offset: -4,
            font: { size: 12 },
          },
        },
      ],
    };
  } else {
    const intentSegments =
      metric === "intent" && stacked
        ? (Array.isArray(intentSegmentOrder) && intentSegmentOrder.length
            ? intentSegmentOrder
            : sortedData.reduce((acc, item) => {
                const keys = Object.keys(item.intent || {});
                for (const k of keys) {
                  if (!acc.includes(k)) acc.push(k);
                }
                return acc;
              }, []))
        : [];

    chartData = {
      labels,
      datasets:
        metric === "intent" && stacked
          ? intentSegments.map((segmentLabel) => ({
              label: segmentLabel,
              data: sortedData.map(
                (item) => item.intent?.[segmentLabel]?.percentage ?? 0,
              ),
              backgroundColor: intentLabelToStackedBarRgba(segmentLabel, 0.8),
              barThickness,
              categoryPercentage: 1.0,
              barPercentage: 0.7,
              borderRadius: 5,
            }))
          : metric === "participation"
          ? [
              {
                label: participationDatasetLabel || t("dashboard_charts.participation", "Participation"),
                data: sortedData.map(
                  (item) => item.participation?.value ?? item.count ?? 0,
                ),
                backgroundColor: sortedData.map(
                  (item) => item.participation?.color || "#6A0DAD",
                ),
                barThickness,
                categoryPercentage: 1.0,
                barPercentage: 0.7,
                borderRadius: 5,
              },
            ]
          : stacked
        ? [
            {
              label: t("feedback_dropdown_values.4"),
              data: sortedData.map((item) => item[metric]?.["0.0"]?.percentage),
              backgroundColor: "rgba(220, 37, 37, 1)",
              barThickness,
              categoryPercentage: 1.0,
              barPercentage: 0.7,
              borderRadius: 5,
            },
            {
              label: t("feedback_dropdown_values.3"),
              data: sortedData.map((item) => item[metric]?.["0.5"]?.percentage),
              backgroundColor: "rgba(255, 255, 0, 0.8)",
              barThickness,
              categoryPercentage: 1.0,
              barPercentage: 0.7,
              borderRadius: 5,
            },
            {
              label: t("feedback_dropdown_values.2"),
              data: sortedData.map((item) => item[metric]?.["1.0"]?.percentage),
              backgroundColor: "rgba(16, 200, 0, 0.8)",
              barThickness,
              categoryPercentage: 1.0,
              barPercentage: 0.7,
              borderRadius: 5,
            },
            {
              label: t("feedback_dropdown_values.1"),
              data: sortedData.map((item) => item[metric]?.["-1"]?.percentage),
              backgroundColor: "rgba(224, 131, 17, 0.664)",
              barThickness,
              categoryPercentage: 1.0,
              barPercentage: 0.7,
              borderRadius: 5,
            },
          ]
        : [
            {
              label: t("label.18"),
              data: values.map((val) => val?.value),
              backgroundColor: theme === "dark" ? "#888" : "#4400AA",
              barThickness,
              categoryPercentage: 1.0,
              barPercentage: 0.7,
              borderRadius: 5,
            },
          ],
    };
  }

  const baseScales = {
    y: {
      stacked,
      grid: {
        borderColor:
          theme === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
        color: theme === "dark" ? "white" : "black",
        display: true,
        drawOnChartArea: false,
        drawTicks: false,
        offset: true,
      },
      title: {
        display:
          metric === "sentiment" ||
          metric === "intent" ||
          metric === "participation",
        text:
          metric === "intent"
            ? `${t("label.19")} (%)`
            : metric === "participation"
              ? participationDatasetLabel || t("dashboard_charts.participation", "Participation")
              : `${t("label.18")} (%)`,
        color: theme === "dark" ? "white" : "black",
      },
      color: theme === "dark" ? "white" : "black",
      scaleLabel:
        (metric === "sentiment" && `${t("label.18")} (%)`) ||
        (metric === "intent" && `${t("label.19")} (%)`),
      axis: "y",
      display: true,
      ticks: {
        beginAtZero: true,
        ...(metric === "sentiment" || metric === "intent"
          ? { suggestedMax: 100 }
          : {}),
        stepSize:
          metric === "sentiment" || metric === "intent"
            ? 20
            : metric === "participation"
              ? undefined
              : 1,
        color: theme === "dark" ? "white" : "black",
        font: { size: 12 },
        maxTicksLimit: 6,
      },
    },
    x: {
      stacked,
      border: { display: false },
      ticks: {
        display: true,
        autoSkip: false,
        padding: 10,
        maxRotation: 60,
        minRotation: 60,
        color: theme === "dark" ? "white" : "black",
        font: { size: 10 },
        displayColors: false,
        callback: (val) => {
          const label = labels[val];
          const trimmed = removeParentFromChildren(label);
          if (trimmed !== "") {
            return trimmed.length > 12
              ? trimmed.substr(0, 12) + "..."
              : trimmed;
          }
          return label.length > 12 ? label.substr(0, 12) + "..." : label;
        },
      },
      grid: {
        borderColor:
          theme === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
        color: theme === "dark" ? "white" : "black",
        display: true,
        drawOnChartArea: false,
        drawTicks: false,
        offset: false,
      },
      color: theme === "dark" ? "white" : "black",
    },
  };

  if (metric === "sentiment" || metric === "intent") {
    baseScales.y.max = 100;
  }

  if (metric === "participation") {
    const counts = sortedData.map(
      (item) => item.participation?.value ?? item.count ?? 0,
    );
    const maxV = counts.length ? Math.max(...counts, 1) : 1;
    const rough = maxV / 5;
    const pow10 = 10 ** Math.floor(Math.log10(Math.max(rough, 1)));
    const err = rough / pow10;
    let nice = 10;
    if (err <= 1) nice = 1;
    else if (err <= 2) nice = 2;
    else if (err <= 5) nice = 5;
    const step = nice * pow10;
    let axisMax = Math.ceil((maxV * 1.08) / step) * step;
    if (axisMax <= maxV) axisMax = maxV + step;
    baseScales.y.max = axisMax;
    baseScales.y.ticks.stepSize = step;
    baseScales.y.ticks.precision = 0;
  }

  if (metric === "rating") {
    baseScales.y1 = {
      type: "linear",
      position: "right",
      title: {
        display: true,
        text: t("dashboard_charts.avg_rating", "Avg Rating"),
        font: { size: 14 },
        color: theme === "dark" ? "white" : "black",
      },
      ticks: {
        display: true,
        beginAtZero: true,
        suggestedMin: 0,
        suggestedMax: 10,
        stepSize: 1,
        color: theme === "dark" ? "white" : "black",
        font: { size: 12 },
      },
      grid: { drawOnChartArea: false },
      display: true,
    };
    baseScales.y.title.text = t("dashboard_charts.reviews_count", "Reviews Count");
    baseScales.y.title.color = theme === "dark" ? "white" : "black";
    baseScales.y.ticks.color = theme === "dark" ? "white" : "black";
  }

  const useStackedMixedInnerExternalTooltip =
    stacked && (metric === "intent" || metric === "sentiment");

  const chartOptions = {
    scales: baseScales,
    responsive: true,
    maintainAspectRatio: false,
    bar: { borderRadius: 15 },
    plugins: {
      legend: false,
      tooltip: useStackedMixedInnerExternalTooltip
        ? {
            enabled: false,
            mode: "index",
            intersect: false,
            external: renderStackedMixedInnerExternalTooltip,
          }
        : {
            enabled: true,
            mode: "index",
            intersect: false,
            callbacks: {
              title: function(tooltipItems) {
                const dataIndex = tooltipItems[0].dataIndex;
                const topicData = sortedData[dataIndex];
                if (topicData && topicData.label) {
                  const count = topicData.count || 0;
                  return [
                    `${topicData.label}`,
                    `${t("common.total_reviews")}: ${count}`,
                  ];
                }
                return tooltipItems[0].label;
              },
              label: function(context) {
                const label = context.dataset.label || context.label;
                const value = context.parsed.y || context.raw;

                if (metric === "rating") {
                  if (context.dataset.type === "bar") {
                    return `${label}: ${value}`;
                  }
                  return `${label}: ${
                    typeof value === "number" ? value.toFixed(1) : value
                  }`;
                }

                if (metric === "participation") {
                  return `${label}: ${value}`;
                }

                return `${label}: ${value}%`;
              },
            },
          },
      datalabels: {
        display: metric === "rating",
      },
    },
    onHover: (event, chartElement) => {
      event.native.target.style.cursor = chartElement.length
        ? "pointer"
        : "default";
    },
    onClick: (event) => {
      if (
        typeof onSentimentStackSegmentClick === "function" &&
        metric === "sentiment" &&
        stacked &&
        chartRef.current
      ) {
        const activeElement = chartRef.current.getElementsAtEventForMode(
          event,
          "nearest",
          { intersect: true },
          true
        );
        if (activeElement?.length > 0) {
          const { index, datasetIndex } = activeElement[0];
          onSentimentStackSegmentClick(index, datasetIndex);
          return;
        }
      }
      if (
        typeof onStackedBarClick === "function" &&
        metric === "sentiment" &&
        stacked &&
        chartRef.current
      ) {
        const activeElement = chartRef.current.getElementsAtEventForMode(
          event,
          "nearest",
          { intersect: true },
          true
        );
        if (activeElement?.length > 0) {
          onStackedBarClick(activeElement[0].index);
          return;
        }
      }
      if (disableSelection) return;
      if (chartRef.current) {
        const activeElement = chartRef.current.getElementsAtEventForMode(
          event,
          "nearest",
          { intersect: true },
          true
        );
        if (activeElement?.length > 0) {
          const clickedIndex = activeElement[0].index;
          if (children[clickedIndex]?.length > 0) {
            if (Object.keys(selectedTopics).length === 0) {
              setSelectedTopics({
                [ids[clickedIndex]]: {
                  level: 1,
                  id: ids[clickedIndex],
                  label: labels[clickedIndex],
                },
              });
            } else {
              setSelectedTopics((prev) => {
                const prevLevels = Object.values(prev).map(
                  (item) => item.level
                );
                const maxLevel = Math.max(...prevLevels);
                const newLevel =
                  maxLevel + (prevLevels.includes(maxLevel) ? 1 : 0);
                return {
                  ...prev,
                  [ids[clickedIndex]]: {
                    level: newLevel,
                    id: ids[clickedIndex],
                    label: labels[clickedIndex],
                  },
                };
              });
            }
          }
        }
      }
    },
  };

  const scrollContainerStyles = shouldEnableScrolling()
    ? {
        overflowX: "auto",
        overscrollBehaviorX: "contain",
        width: "100%",
        height: "100%",
        scrollbarWidth: "thin",
        scrollbarColor: theme === "dark" ? "#555 #333" : "#ccc #f1f1f1",
        padding: "10px 0",
      }
    : {
        width: "100%",
        height: "100%",
        padding: "10px 0",
      };

  const chartContainerStyles = {
    width: getChartWidth(),
    height: "100%",
    minWidth: shouldEnableScrolling() ? getChartWidth() : "100%",
    padding: shouldEnableScrolling() ? "0 20px" : "0",
  };

  return (
    <div className={styles.barChartWrapper}>
      {scrollable && (
        <div className={styles.actionBox}>
          {shouldEnableScrolling() && (
            <div className={styles.hintMsg}>
              {isMobileView
                ? "Swipe to see all topics →"
                : "Scroll to see all topics →"}
            </div>
          )}
        </div>
      )}
      {intentLegendSegments.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "8px 18px",
            padding: "0 8px 10px",
          }}
        >
          {intentLegendSegments.map((segmentLabel) => (
            <span
              key={segmentLabel}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                lineHeight: 1.4,
                color: theme === "dark" ? "#fff" : "#100d31",
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  flexShrink: 0,
                  backgroundColor: intentLabelToStackedBarRgba(segmentLabel, 0.8),
                }}
              />
              {segmentLabel}
            </span>
          ))}
        </div>
      )}
      <div ref={containerRef}>
        <div
          className={`${styles.fadeOverlay} ${styles.leftFade}`}
          style={{
            opacity: shouldEnableScrolling() && isScrolling ? 0.7 : 0,
          }}
        />
        <div
          className={`${styles.fadeOverlay} ${styles.rightFade}`}
          style={{
            opacity: shouldEnableScrolling() && isScrolling ? 0.7 : 0,
          }}
        />
        <div ref={scrollContainerRef} style={scrollContainerStyles}>
          <div style={chartContainerStyles}>
            <CustomChart
              ref={chartRef}
              type="bar"
              data={chartData}
              options={chartOptions}
              plugins={metric === "rating" ? ["chartjs-plugin-datalabels"] : []}
              width={null}
              height={null}
              dependency={JSON.stringify(sortedData)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarChartWrapper;
